/* Клиенты (PRO-8, PRO-9): рабочая таблица во всю ширину — сортировка, поиск, фильтры.
   Клик по строке — карточка клиента; ссылка на группу в строке работает сама. */
import { norm } from '@tg/domain';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trpc } from '@tg/app-core';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hasProg, lastActive, useClients, type ClientRow } from '../lib/data';
import { age, ago, daysBetween, dm, dshort, ini, plural, today } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { MSort, useNilCells } from '../shell/Mobile';
import { ICON } from '../ui/icons';
import { GroupPick } from '../ui/GroupPick';
import { InviteLink, InviteModal } from '../ui/InviteLink';

const V = { q: '', f: 'all' };
const CS: { k: string | null; dir: number } = { k: null, dir: 1 };
/** Поля сортировки — те же, что у шапки таблицы; на телефоне шапки нет, выбор строкой MSort */
const SORT_COLS = [
  { k: 'n', n: 'Клиент' },
  { k: 'grp', n: 'Группа' },
  { k: 'pay', n: 'Оплата' },
  { k: 'done', n: 'Тренировки' },
  { k: 'last', n: 'Последняя активность' },
  { k: 'add', n: 'Дата добавления' },
];
/** Дата добавления — день в часовом поясе браузера */
const dayOf = (d: Date | string) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const TRN_TIP = 'Всего тренировок / выполнено / процент выполнения. Всего — опубликованные клиенту по сегодня, выполнено — завершённые им в приложении.';

const idle = (c: ClientRow) => {
  const l = lastActive(c);
  return !!l && daysBetween(l, today()) >= 3;
};
const needsAttention = (c: ClientRow) => c.unread > 0 || (hasProg(c) && idle(c));

/* Оплата: просрочена, заканчивается (неделя и меньше), оплачено; даты нет — не внесена */
function PayCell({ c }: { c: ClientRow }) {
  const d = c.paidUntil;
  const T = today();
  const r = d ? daysBetween(T, d) : 0;
  const st = !d ? 'none' : r < 0 ? 'late' : r <= 7 ? 'soon' : 'ok';
  const [cls, t] = ({ none: ['ghost', 'не внесена'], late: ['rm', 'просрочена'], soon: ['warn', r === 0 ? 'последний день' : 'ещё ' + r + ' ' + plural(r, 'день', 'дня', 'дней')], ok: ['ok', 'оплачено'] } as const)[st];
  const date = d ? dm(d) + (d.slice(0, 4) !== T.slice(0, 4) ? '.' + d.slice(0, 4) : '') : '';
  return (
    <div className="paycell">
      <div>
        <span className={'chip ' + cls}>{t}</span>
        {d && (
          <span className="pd">
            {st === 'late' ? 'истекла' : 'до'} {date}
          </span>
        )}
      </div>
    </div>
  );
}


export function Clients() {
  const cs = useClients().data;
  usePageTitle('Клиенты', cs?.length ?? null);
  const nav = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  useNilCells(box);
  const [, redraw] = useState(0);
  const [inv, setInv] = useState(false);
  const [gp, setGp] = useState<ClientRow | null>(null);
  const [menu, setMenu] = useState<{ c: ClientRow; el: HTMLElement } | null>(null);
  const [act, setAct] = useState<{ c: ClientRow; k: 'freeze' | 'del' } | null>(null);
  /* Пришли из «Первых шагов» (?onb=invite) — подсвечиваем, где лежит ссылка-приглашение */
  const [sp, setSp] = useSearchParams();
  const spot = sp.get('onb') === 'invite';
  const set = (f: () => void) => {
    f();
    redraw((x) => x + 1);
  };
  const all = cs || [];
  if (V.f === 'frozen' && cs && !all.some((c) => c.frozenAt)) V.f = 'all';
  const T = today();
  let list = all.filter((c) => {
    /* Замороженные — только в своём фильтре, в остальных их нет (не занимают место, ничего с ними не делаем) */
    if ((V.f === 'frozen') !== !!c.frozenAt) return false;
    if (V.f === 'nogrp' && c.group) return false;
    if (V.f === 'grp' && !c.group) return false;
    if (V.f === 'attn' && !needsAttention(c)) return false;
    if (V.q && norm(c.name).indexOf(norm(V.q)) < 0) return false;
    return true;
  });
  const SORTS: Record<string, (a: ClientRow, b: ClientRow) => number> = {
    n: (a, b) => a.name.localeCompare(b.name, 'ru'),
    /* Без группы — в конце */
    grp: (a, b) => (a.group?.name ?? '\uffff').localeCompare(b.group?.name ?? '\uffff', 'ru'),
    done: (a, b) => (a.done / a.planned || 0) - (b.done / b.planned || 0) || a.planned - b.planned,
    add: (a, b) => +new Date(a.createdAt) - +new Date(b.createdAt),
    last: (a, b) => String(lastActive(a) || '').localeCompare(String(lastActive(b) || '')),
    pay: (a, b) => String(a.paidUntil || '9999').localeCompare(String(b.paidUntil || '9999')),
  };
  if (CS.k && SORTS[CS.k]) list = [...list].sort((a, b) => SORTS[CS.k!](a, b) * CS.dir);
  const FILTERS = [
    ['all', 'Все'],
    ['nogrp', 'Без группы'],
    ['grp', 'В группах'],
    ['attn', 'Требуют внимания'],
    ...(all.some((c) => c.frozenAt) ? [['frozen', 'Замороженные']] : []),
  ];
  const th = (k: string | null, n: React.ReactNode) => (
    <th
      className={(k ? 'srt ' : '') + (CS.k === k && k ? (CS.dir > 0 ? 'up' : 'dn') : '')}
      onClick={
        k
          ? () =>
              set(() => {
                if (CS.k === k) CS.dir = -CS.dir;
                else {
                  CS.k = k;
                  CS.dir = 1;
                }
              })
          : undefined
      }
    >
      {n}
    </th>
  );

  return (
    <div className="cons one">
      <main>
        <section className="pane" id="page">
          <div className="pane-h">
            <label className="search" style={{ width: 260 }}>
              <ICON.search />
              <input id="q" placeholder="Поиск по имени…" autoFocus value={V.q} onChange={(e) => set(() => (V.q = e.target.value))} />
            </label>
            <div className="filters">
              {FILTERS.map(([k, n]) => (
                <button key={k} className={V.f === k ? 'on' : ''} onClick={() => set(() => (V.f = k))}>
                  {n}
                </button>
              ))}
            </div>
            <span className="sp"></span>
            <span className={'invw' + (spot ? ' spot' : '')}>
              <button
                className="btn"
                id="btnInvite"
                onClick={() => {
                  if (spot) setSp((p) => (p.delete('onb'), p), { replace: true });
                  setInv(true);
                }}
              >
                <ICON.plus /> Пригласить клиента
              </button>
              {spot && <span className="spottip">Ваша ссылка-приглашение — здесь</span>}
            </span>
          </div>
          {!cs ? null : list.length ? (
            <div style={{ display: 'contents' }} ref={box}>
            <MSort cols={SORT_COLS} cur={CS.k} dir={CS.dir} onSort={(k) => set(() => (CS.k === k ? (CS.dir = -CS.dir) : ((CS.k = k), (CS.dir = 1))))} />
            <div className="tblwrap">
              <table className="tbl" data-tbl="cli">
                <thead>
                  <tr>
                    {th('n', 'Клиент')}
                    {th('grp', 'Группа')}
                    {th('pay', 'Оплата')}
                    {th(
                      'done',
                      <>
                        Тренировки{' '}
                        <span className="qh" tabIndex={0} role="button" aria-label="Что в колонке" data-tip={TRN_TIP} onClick={(e) => e.stopPropagation()}>
                          ?
                        </span>
                      </>,
                    )}
                    {th('last', 'Последняя активность')}
                    {th('add', 'Дата добавления')}
                    <th className="cact"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => {
                    const a = age(c.born);
                    const la = lastActive(c);
                    return (
                      <tr
                        key={c.id}
                        className={'click' + (c.frozenAt ? ' frz' : '')}
                        data-row={c.id}
                        onClick={(e) => {
                          if ((e.target as Element).closest('a')) return;
                          nav('/clients/' + c.id);
                        }}
                      >
                        <td>
                          <div className="cellname">
                            <span className="av m">{c.photo ? <img src={c.photo} alt="" /> : ini(c.name)}</span>
                            <div>
                              <div className="n">
                                {c.name}
                                {c.unread > 0 && (
                                  <>
                                    {' '}
                                    <span className="chip acc">{c.unread}</span>
                                  </>
                                )}
                                {c.frozenAt && (
                                  <>
                                    {' '}
                                    <span className="chip frz" title="Не занимает место в тарифе. Разморозить — в карточке клиента">
                                      заморожен
                                    </span>
                                  </>
                                )}
                                {c.overLimit && (
                                  <>
                                    {' '}
                                    <span className="chip over" title="Пока не изменён лимит тарифа, составлять тренировки нельзя">
                                      сверх лимита
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="s">{[a != null ? a + ' лет' : '', c.sport, c.level].filter(Boolean).join(' · ')}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {c.group ? (
                            <span className="gchips">
                              <Link className="gchip" to={'/groups?id=' + c.group.id} title={`Группа «${c.group.name}» — открыть`}>
                                <ICON.grpSm />
                                {c.group.name}
                              </Link>
                            </span>
                          ) : c.frozenAt ? (
                            <span style={{ color: 'var(--tx3)' }}>нет группы</span>
                          ) : (
                            <button className="btn gh sm grpadd" title="Добавить клиента в группу" onClick={(e) => (e.stopPropagation(), setGp(c))}>
                              <ICON.plus /> В группу
                            </button>
                          )}
                        </td>
                        <td>
                          <PayCell c={c} />
                        </td>
                        <td>
                          {c.planned ? (
                            <span className="trn">
                              {c.planned} <s>/</s> {c.done} <s>/</s> <b>{Math.round((Math.min(c.done, c.planned) / c.planned) * 100)}%</b>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--tx3)' }}>—</span>
                          )}
                        </td>
                        <td>{la ? <span className={'chip ' + (daysBetween(la, T) >= 3 ? 'warn' : 'ok')}>{ago(la)}</span> : <span className="chip ghost">не заходил</span>}</td>
                        <td className="mut">{dshort(dayOf(c.createdAt))}</td>
                        <td className="cact">
                          <button
                            className="iact cmore"
                            title="Действия"
                            aria-label="Действия"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenu({ c, el: e.currentTarget });
                            }}
                          >
                            ⋯
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </div>
          ) : (
            <div className="empty">
              {all.length ? (
                <>
                  <div className="t">Никого не нашли</div>
                  <div className="d">Измените поиск или фильтр</div>
                </>
              ) : (
                <>
                  <div className="t">У вас пока нет клиентов</div>
                  <div className="d">Отправьте ссылку-приглашение — новый клиент появится здесь</div>
                  <InviteLink />
                </>
              )}
            </div>
          )}
        </section>
      </main>
      {inv && <InviteModal onClose={() => setInv(false)} />}
      {menu && <RowMenu c={menu.c} el={menu.el} onClose={() => setMenu(null)} onPick={(k) => (setMenu(null), setAct({ c: menu.c, k }))} />}
      {act && <RowAction c={act.c} k={act.k} onClose={() => setAct(null)} />}
      {gp && <GroupPick client={{ id: gp.id, name: gp.name, groupId: gp.group?.id ?? null }} onClose={() => setGp(null)} />}
    </div>
  );
}

/* «⋯» в строке клиента: заморозить / разморозить и удалить — как в карточке клиента */
function RowMenu({ c, el, onClose, onPick }: { c: ClientRow; el: HTMLElement; onClose: () => void; onPick: (k: 'freeze' | 'del') => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const off = (e: Event) => !box.current?.contains(e.target as Node) && e.target !== el && onClose();
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', key);
    addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', key);
      removeEventListener('scroll', onClose, true);
    };
  }, [el, onClose]);
  const r = el.getBoundingClientRect();
  return createPortal(
    <div ref={box} className="sug cmenu" style={{ position: 'fixed', top: r.bottom + 4, left: Math.max(8, r.right - 220) }}>
      <button className="row" onClick={() => onPick('freeze')}>
        <b>{c.frozenAt ? 'Разморозить' : 'Заморозить'}</b>
      </button>
      <button className="row rm" onClick={() => onPick('del')}>
        <b>Удалить</b>
      </button>
    </div>,
    document.body,
  );
}

function RowAction({ c, k, onClose }: { c: ClientRow; k: 'freeze' | 'del'; onClose: () => void }) {
  const utils = trpc.useUtils();
  const done = (msg: string) => {
    utils.trainer.clients.invalidate();
    utils.trainer.me.invalidate();
    utils.trainer.groups.invalidate();
    utils.trainer.days.invalidate();
    toast(msg);
    onClose();
  };
  const err = (e: { message?: string }) => toast(e.message || 'Не получилось');
  const freeze = trpc.trainer.freezeClient.useMutation({ onSuccess: (_r, v) => done(v.frozen ? 'Клиент заморожен — место в тарифе освободилось' : 'Клиент снова активен'), onError: err });
  const del = trpc.trainer.deleteClient.useMutation({ onSuccess: () => done('Клиент удалён'), onError: err });
  if (k === 'freeze' && c.frozenAt)
    return (
      <Modal
        title={'Разморозить · ' + c.name}
        onClose={onClose}
        foot={
          <>
            <span className="sp"></span>
            <button className="btn gh" onClick={onClose}>
              Отмена
            </button>
            <button className="btn" disabled={freeze.isPending} onClick={() => freeze.mutate({ id: c.id, frozen: false })}>
              Разморозить
            </button>
          </>
        }
      >
        <p className="lead">Клиент снова займёт место в тарифе — ему можно составлять тренировки, вести оплату и группу.</p>
      </Modal>
    );
  if (k === 'freeze')
    return (
      <Modal
        title={'Заморозить · ' + c.name}
        onClose={onClose}
        foot={
          <>
            <span className="sp"></span>
            <button className="btn gh" onClick={onClose}>
              Отмена
            </button>
            <button className="btn" disabled={freeze.isPending} onClick={() => freeze.mutate({ id: c.id, frozen: true })}>
              Заморозить
            </button>
          </>
        }
      >
        <p className="lead">
          Клиент перестанет занимать место в тарифе. Тренировки, результаты, рекорды и переписка сохранятся — их можно смотреть, но составлять новое нельзя{c.group ? ', из группы клиент выйдет' : ''}. Разморозить можно в любой момент, если в тарифе есть место.
        </p>
      </Modal>
    );
  return (
    <Modal
      title={'Удалить · ' + c.name}
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn rm" disabled={del.isPending} onClick={() => del.mutate({ id: c.id })}>
            Удалить навсегда
          </button>
        </>
      }
    >
      <p className="lead">
        Сотрётся всё: тренировки, результаты, замеры, рекорды и переписка.{c.registered ? ' Приложение клиента выйдет из аккаунта — вернуться к вам он сможет только по новой ссылке-приглашению.' : ''} Отменить нельзя.
        {!c.frozenAt && ' Если клиент просто на паузе — лучше заморозить: место в тарифе освободится, а история останется.'}
      </p>
    </Modal>
  );
}
